/**
 * AuthSync — глобальный слушатель событий сессии Supabase (C1 в аудите).
 *
 * Зачем нужен:
 *   Сейчас прила узнаёт о логине/логауте только через свои хуки (useSignIn/
 *   useSignOut). Но сессия может меняться "снаружи":
 *     - юзер вышел в другой вкладке → здесь он всё ещё видит свои данные
 *     - юзер вошёл в другой вкладке → здесь он считается разлогиненным
 *     - refresh token отвалился / админ убил сессию → юзер сидит с мёртвым
 *       токеном, запросы валятся с 401
 *     - OAuth callback → новая сессия появляется "из воздуха"
 *
 * Решение по стандарту индустрии (Supabase docs, NextAuth, Clerk):
 *   глобально подписаться на supabase.auth.onAuthStateChange и реагировать
 *   на изменения. Упрощённая стратегия:
 *     - сессия появилась / обновилась → invalidate currentUser (React Query
 *       решит сам нужен ли реальный фетч на основе staleTime)
 *     - сессия пропала → clear() + редирект на sign-in
 *
 * Почему именно так (а не с матчингом конкретных событий SIGNED_IN /
 * TOKEN_REFRESHED / USER_UPDATED):
 *   меньше веток = меньше багов. React Query с staleTime: 10min сам решит
 *   делать ли реальный запрос, так что лишних фетчей не будет.
 *
 * Размещение: внутри RouterProviders — нужен router context для useNavigate
 * и QueryProvider для useQueryClient.
 *
 * Существующие useSignIn/useSignOut не трогаем: они делают свою навигацию
 * и инвалидацию синхронно в onSuccess, что даёт моментальный UI-отклик.
 * AuthSync — страховочный слой для edge cases, описанных выше.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { supabase } from '@/lib';
import { ROUTE_PATHS, userQueryKeys } from '@/lib';

export function AuthSync(): null {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // PASSWORD_RECOVERY — особый случай. Юзер пришёл по ссылке из письма
      // "восстановить пароль", временная сессия выдана только для вызова
      // updateUser(). Не трогаем — ResetPasswordPage это обрабатывает сам,
      // а наш редирект на sign-in выкинет юзера с формы восстановления.
      if (event === 'PASSWORD_RECOVERY') {
        return;
      }

      if (session) {
        // Сессия есть — синхронизируем currentUser.
        // invalidate не делает немедленный запрос: React Query сравнит с
        // staleTime (10min для currentUser) и запросит только если данные
        // устарели. При TOKEN_REFRESHED обычно никакой фетч не выполняется.
        void queryClient.invalidateQueries({ queryKey: userQueryKeys.currentUser() });
      } else {
        // Сессии нет — юзер разлогинен (кнопкой, в другой вкладке, или
        // refresh token отвалился). Чистим кэш и отправляем на sign-in.
        queryClient.clear();
        navigate(ROUTE_PATHS.SIGN_IN, { replace: true });
      }
    });

    // Отписка при размонтировании. В dev со StrictMode эффект вызывается
    // дважды, но cleanup корректно отписывает предыдущую регистрацию.
    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, navigate]);

  return null;
}
